import { useEffect, useMemo, useRef } from 'react';
import { FileText, FolderOpen } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import type { KnowledgeBaseVaultPage } from '@/components/knowledge/types';

interface VaultPageListProps {
  pages: KnowledgeBaseVaultPage[];
  selectedPageId: string | null;
  onSelectPage: (page: KnowledgeBaseVaultPage) => void;
}

function groupPages(pages: KnowledgeBaseVaultPage[]) {
  const groups = new Map<string, KnowledgeBaseVaultPage[]>();

  for (const page of pages) {
    const folder = page.path.includes('/') ? page.path.split('/')[0] : 'root';
    const existing = groups.get(folder);
    if (existing) {
      existing.push(page);
    } else {
      groups.set(folder, [page]);
    }
  }

  return Array.from(groups.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([folder, folderPages]) => [folder, folderPages.sort((a, b) => a.path.localeCompare(b.path))] as const);
}

export default function VaultPageList({ pages, selectedPageId, onSelectPage }: VaultPageListProps) {
  const groups = useMemo(() => groupPages(pages), [pages]);
  const itemRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  useEffect(() => {
    if (!selectedPageId) {
      return;
    }

    itemRefs.current[selectedPageId]?.scrollIntoView({ block: 'nearest' });
  }, [selectedPageId]);

  return (
    <ScrollArea className="h-full min-h-0 rounded-lg border bg-card p-3">
      <div className="space-y-4">
        {groups.map(([folder, folderPages]) => (
          <div key={folder} className="space-y-2">
            <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              <FolderOpen className="h-3.5 w-3.5" />
              {folder}
            </div>
            <div className="space-y-1">
              {folderPages.map((page) => (
                <button
                  key={page.id}
                  ref={(node) => {
                    itemRefs.current[page.id] = node;
                  }}
                  type="button"
                  onClick={() => onSelectPage(page)}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors hover:bg-muted',
                    page.id === selectedPageId && 'bg-muted text-foreground'
                  )}
                >
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium">{page.title}</div>
                    <div className="truncate text-xs text-muted-foreground">{page.path}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </ScrollArea>
  );
}